import _ from 'lodash';
import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import TextareaAutosize from 'react-autosize-textarea';
import useEditorStore from '../../state-hook';
import useUpdateDisclaimer from '../../../../../services/wire/disclaimer-service';
import { usePostUpdateDisclaimer } from '../../../../../services/profiles/contributor-settings-service';

const Style = createGlobalStyle`
  .modal article.horizontal {
    width: 850px;
    max-height: 420px;
  }
  .modal-body.modal-body.modal-body.modal-body.modal-body {
    flex-grow: 2;
    padding-left: 0px;
    padding-right: 0px;
    display: flex;
    flex-direction: column;
    align-items: center;

    > strong {
      margin-top: 20px;
      font-size: 24px !important;
      line-height: 29px;
    }

    > #desc {
      font-size: 19px;
      line-height: 23px;
      margin: 1em 2em;
    }
  }

  .modal-footer {
    flex-basis: 90px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:after {
      order: 0;
      content: "";
      width: 26px;
      display: block;
    }

    > button {
      margin: 0 8px;

      .btn-inverted {
        order: 1;
        border-radius: 4px;
        border: 1px solid ${props => props.theme.secondaryColor};
      }
      .btn-primary {
        order: -1;
        width: 152px;
      }
    }
  }
`;

const TextArea = styled(TextareaAutosize)`
  font-family: ${props => props.theme.fontFamilyBody};
  font-size: 14px;
  line-height: 18px;
  padding: 0;
  resize: none;
  overflow: hidden;
  height: 160px;
  margin: 1em 2em;
  width: 90%;
`;

const Checkbox = styled.label`
  font-size: 16px;
  margin-bottom: 8px;
`;

const AddDisclaimerPromptModal = React.forwardRef((props, ref) => {
  const wire = useEditorStore(state => state.wire);
  const set = useEditorStore(state => state.set);
  const currentProfile = useEditorStore(state => state.currentProfile);
  const [intention, setIntention] = React.useState('');
  const [show, setShow] = React.useState(false);
  const [disclaimer, setDisclaimer] = React.useState('');
  const [assignToAllWires, setAssignToAllWires] = React.useState(true);
  const [, updateDisclaimer] = useUpdateDisclaimer();
  const [, updateMyDisclaimer] = usePostUpdateDisclaimer();
  const contentRef = React.useRef();

  React.useEffect(() => {
    show && contentRef.current && contentRef.current.focus();
  }, [show]);

  const getButtonText = intent => {
    const requiresWireReview = currentProfile?.requiresWireReview === true;
    const isWirePublished = wire?.published_at != null;
    if (requiresWireReview && !isWirePublished && intent === 'publish') {
      return 'Submit';
    }

    // Format intention text (e.g., 'publish' -> 'Publish wire', 'schedule' -> 'Schedule wire')
    if (intent === 'publish') {
      return 'Publish';
    }
    if (intent === 'schedule') {
      return 'Schedule';
    }
    if (intent === 'update') {
      return 'Update';
    }

    return intent;
  };

  const present = d => {
    const profile = _.get(wire, 'profile', {});
    if (_.get(wire, 'disclaimer') || _.get(currentProfile, 'slug') !== profile.slug) {
      props.onDismiss(d);
    } else {
      setIntention(d);
      setShow(true);
    }
  };

  const goBack = () => {
    setShow(false);
    props.onDismiss('invalid');
  };

  const proceed = async () => {
    if (disclaimer.length > 0) {
      await updateDisclaimer(wire.id, disclaimer);
      set(state => {
        state.wire.disclaimer = disclaimer;
      });
      if (assignToAllWires) {
        await updateMyDisclaimer(disclaimer);
        set(state => {
          state.currentProfile.disclaimer = disclaimer;
        });
      }
    }
    setShow(false);
    props.onDismiss(intention);
  };

  const onChange = e => {
    e.preventDefault();
    setDisclaimer(e.target.value);
  };

  const toggleCheckbox = e => {
    setAssignToAllWires(e.target.checked);
  };

  React.useImperativeHandle(ref, () => ({ present }));

  return (
    <Modal
      centered="true"
      dialogAs="article"
      dialogClassName="horizontal"
      className="disclaimer-modal"
      show={show}
      onHide={proceed}
      backdrop="static"
    >
      <Style />
      <Modal.Body className="main">
        <strong className="title">Would you like to add a disclaimer?</strong>
        <p id="desc">
          Including a disclaimer on your wires is not required, however we do recommend
          that you include one or check with your compliance team as to whether a
          disclaimer is required. If you hold an AFSL, we also recommend you include
          this in your disclaimer section.
        </p>
        <TextArea
          ref={contentRef}
          value={disclaimer}
          onChange={onChange}
          placeholder="Type or paste disclaimer here"
          maxRows={8}
        />
        <Checkbox>
          <input
            type="checkbox"
            checked={!!assignToAllWires}
            onChange={toggleCheckbox}
          />
          Assign this disclaimer to all wires I
          {' '}
          {getButtonText(intention).toLowerCase()}
        </Checkbox>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary-inverted" onClick={goBack}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={proceed}>
          {getButtonText(intention)}
        </button>
      </Modal.Footer>
    </Modal>
  );
});

AddDisclaimerPromptModal.propTypes = { onDismiss: PropTypes.func.isRequired };

AddDisclaimerPromptModal.displayName = 'AddDisclaimerPromptModal';

export default AddDisclaimerPromptModal;
